import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fr, Translations } from './translations/fr';
import { en } from './translations/en';
import { de } from './translations/de';

export type Language = 'FR' | 'EN' | 'DE';

const translations: Record<Language, Translations> = {
  FR: fr,
  EN: en,
  DE: de,
};

interface I18nContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('talentees-lang') as Language;
      if (saved && translations[saved]) {
        return saved;
      }
      // Check browser language
      const browserLang = navigator.language.split('-')[0].toUpperCase();
      if (browserLang === 'DE') return 'DE';
      if (browserLang === 'EN') return 'EN';
    }
    return 'FR';
  });

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('talentees-lang', newLang);
    }
  }, []);

  useEffect(() => {
    // Update html lang attribute
    document.documentElement.lang = lang.toLowerCase();
  }, [lang]);

  const value: I18nContextType = {
    lang,
    setLang,
    t: translations[lang],
  };

  return React.createElement(I18nContext.Provider, { value }, children);
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export { translations };
export type { Translations };
